import { useMutation, useQuery } from '@tanstack/react-query'
import { api, customersApi } from '@/lib/api'
import type { TeamOption, TeamStats } from '@/lib/types'
import { useCurrentTeam } from './current-team'

export function useTeams() {
  const { ready } = useCurrentTeam()
  return useQuery({
    queryKey: ['team', 'list'],
    enabled: ready,
    queryFn: async () => (await api.get<TeamOption[]>('/user/teams')).data ?? [],
  })
}

export function useSwitchTeam() {
  return useMutation({
    mutationFn: (teamId: number) => api.post('/team/switch', { teamId }),
    // Every cached query belongs to the old team; a reload is the honest reset.
    onSuccess: () => window.location.reload(),
  })
}

/** Usage against plan limits: users and integrations from the main API, companies from the customers API. */
export function useTeamStats() {
  const { ready } = useCurrentTeam()
  return useQuery({
    queryKey: ['team', 'stats'],
    enabled: ready,
    queryFn: async (): Promise<TeamStats> => {
      const [team, companies] = await Promise.all([
        api.get<{ userCount?: number; activeIntegrations?: Record<string, boolean> }>('/team/stats'),
        customersApi.get<{ count?: number; skipped?: number }>('/customer/companies/stats'),
      ])
      return {
        users: team.data.userCount ?? 0,
        integrations: Object.values(team.data.activeIntegrations ?? {}).filter(Boolean).length,
        companies: companies.data.count ?? 0,
        skipped: companies.data.skipped ?? 0,
      }
    },
  })
}

/** Same rules as v1 CompaniesLimitBanner / MenuCard. */
export function useCompaniesLimit() {
  const { current } = useCurrentTeam()
  const { data: stats } = useTeamStats()
  const limit = current?.limits?.companies
  const max = current?.plan?.Limits?.companies ?? limit?.max ?? null
  const count = stats?.companies ?? limit?.current ?? null
  const reached = limit?.reached === true || (max !== null && count !== null && count >= max)
  return { max: max ?? 0, count: count ?? 0, reached }
}
